/* Storage preferences, persisted in localStorage.
 *
 * The sign-in session is strictly necessary and needs no permission. Analytics is
 * off until the visitor opts in, and nothing non-essential may run unless
 * allowsAnalytics() returns true.
 *
 * Bump CONSENT_VERSION when the categories or the policy change meaning: a stored
 * choice against an older version is treated as unanswered, because consent to a
 * different question is not consent to this one.
 *
 * Filename note: deliberately not consent.ts or cookies.ts. Common ad-blocker
 * filter lists match on request path, so a module with one of those names is
 * blocked outright for some visitors, and a consent banner that silently fails to
 * render is worse than no banner: from our side it looks as though everyone was
 * asked. Same reason for funnel.ts and NoticeBar.tsx.
 */
const KEY = 'srsly_prefs';
export const CONSENT_VERSION = 1;
export const CONSENT_EVENT = 'srsly-prefs-change';

export type Consent = { v: number; ts: string; necessary: true; analytics: boolean };

export function getConsent(): Consent | null {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Consent | null;
    return c && c.v === CONSENT_VERSION ? c : null;
  } catch {
    // Private browsing or storage disabled. Treated as "not asked", never as agreement.
    return null;
  }
}

export const hasDecided = () => getConsent() !== null;
export const allowsAnalytics = () => !!getConsent()?.analytics;

export function setConsent({ analytics = false }: { analytics?: boolean } = {}): Consent {
  const rec: Consent = {
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    necessary: true,
    analytics: !!analytics,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* storage blocked -- honour the choice for this session only */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: rec }));
  return rec;
}

export const acceptAll = () => setConsent({ analytics: true });
export const rejectNonEssential = () => setConsent({ analytics: false });
